import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { Building2 } from "lucide-react";
import { PawmatIconOutlined } from "../../../shared/Icons/CustomIcons.js";
import Counter from "../Hero/Counter.js";
import StatsBox from "../../../Components/StatsBox";
import { apiClient } from "../../../lib/apiClient.js";
import { SparkleHoverBadge } from "../../../Components/Buttons/SparkleHoverButton.js";
import { PAGE_GUTTER_INSET_CLASS } from "../../../shared/PageContainer.js";

const PawMatchBanner = () => {
  const { t } = useTranslation("landing");
  const [kennelCount, setKennelCount] = useState<number>(0);
  const [isLoadingKennels, setIsLoadingKennels] = useState(true);
  const [particularCount, setParticularCount] = useState<number>(0);
  const [isLoadingParticulares, setIsLoadingParticulares] = useState(true);
  const navigate = useNavigate();

  // Fetch kennel count using /api/kennels/stats for accurate totals
  useEffect(() => {
    const fetchKennelCount = async () => {
      try {
        const data = await apiClient.get<{
          success: boolean;
          stats: {
            kennels: { total: number };
          };
        }>("/api/kennels/stats");

        if (data.success && data.stats?.kennels) {
          setKennelCount(data.stats.kennels.total);
        } else {
          setKennelCount(0);
        }
      } catch (error) {
        console.error("Error fetching kennel count:", error);
        setKennelCount(0);
      } finally {
        setIsLoadingKennels(false);
      }
    };

    fetchKennelCount();
  }, []);

  // Fetch the public particulares sub-count for the "Nossos Parceiros" card.
  useEffect(() => {
    const fetchParticularCount = async () => {
      try {
        const data = await apiClient.get<{
          success: boolean;
          stats: {
            particulares: { total: number };
          };
        }>("/api/particulares/stats");

        if (data.success && data.stats?.particulares) {
          setParticularCount(data.stats.particulares.total);
        } else {
          setParticularCount(0);
        }
      } catch (error) {
        console.error("Error fetching particular count:", error);
        setParticularCount(0);
      } finally {
        setIsLoadingParticulares(false);
      }
    };

    fetchParticularCount();
  }, []);

  const handlePawMatchClick = () => {
    navigate("/pawmatch");
  };

  const handleKennelsClick = () => {
    navigate("/canis");
  };

  return (
    <section className=" bg-white border-none pb-20">
      <div
        className={`bg-white flex-row mb-5 text-center text-xl pt-12 sm:pt-16 md:pt-20 ${PAGE_GUTTER_INSET_CLASS}`}
      >
        <h1 className="font-bold text-3xl md:text-4xl pb-3 text-my-black leading-tight">
          {/* Mobile text */}
          <span className="md:hidden">
            <Trans
              t={t}
              i18nKey="banner.headlineMobile"
              components={{
                dog: (
                  <span
                    className="text-main-purple hover:cursor-pointer"
                    onClick={() => navigate("/caes")}
                  />
                ),
                cat: (
                  <span
                    className="text-main-blue hover:cursor-pointer"
                    onClick={() => navigate("/gatos")}
                  />
                ),
              }}
            />
          </span>
          {/* Desktop text */}
          <span className="hidden md:inline">
            <Trans
              t={t}
              i18nKey="banner.headlineDesktop"
              components={{
                dog: (
                  <span
                    className="text-main-purple hover:cursor-pointer"
                    onClick={() => navigate("/caes")}
                  />
                ),
                cat: (
                  <span
                    className="text-main-blue hover:cursor-pointer"
                    onClick={() => navigate("/gatos")}
                  />
                ),
              }}
            />
          </span>
        </h1>
        <span className="leading-tight text-gray-700">
          {/* Mobile text */}
          <span className="md:hidden">{t("banner.subtitleMobile")}</span>
          {/* Desktop text */}
          <span className="hidden md:inline">
            {t("banner.subtitleDesktop")}
          </span>
        </span>
      </div>

      <div className={`py-10 ${PAGE_GUTTER_INSET_CLASS}`}>
        {/* PawTinder Promotional Banner & Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:items-stretch">
          {/* PawTinder Main Banner */}
          <div className="md:col-span-2 h-full">
            <button
              onClick={handlePawMatchClick}
              className="w-full h-full flex items-center justify-center bg-pink-gradient rounded-3xl p-3 text-center shadow-2xl transition-all duration-500 hover:shadow-3xl group relative overflow-hidden"
            >
              <div className="absolute inset-0 rounded-3xl  bg-gradient-to-r from-pink-700 to-rose-800 opacity-0 transition-opacity duration-300" />

              <div className="relative z-10 h-full my-2">
                <PawmatIconOutlined size={200} className="mx-auto" />
                <h2 className="text-4xl font-bold text-golden mb-4">
                  PawMatch
                </h2>
                <p className="text-white/90 text-xl leading-relaxed">
                  {t("banner.pawmatch.tagline")}
                </p>
                <p className="text-white/90 text-lg mb-6 leading-relaxed">
                  {t("banner.pawmatch.description")}
                </p>
                <SparkleHoverBadge
                  className="bg-white/20 border-2 border-golden backdrop-blur-sm hover:scale-105 rounded-xl p-4 text-white transition-all duration-300 hover:bg-transparent hover:text-golden"
                  contentClassName="font-semibold text-lg"
                >
                  {t("banner.pawmatch.cta")}
                </SparkleHoverBadge>
              </div>

              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-[2px] group-hover:translate-x-[2px] transition-transform duration-1000" />
            </button>
          </div>

          {/* Side Features */}
          <div className="h-full flex flex-col gap-6">
            {/* Counter */}
            <div className="flex-1 min-h-0">
              <Counter />
            </div>

            {/* Kennels Counter Card */}
            <div className="flex-1 min-h-0">
              <StatsBox
                icon={<Building2 className="w-12 h-12" />}
                count={kennelCount}
                countLabel={t("banner.partners.countLabel")}
                title={t("banner.partners.title")}
                subtitle={t("banner.partners.subtitle")}
                onClick={handleKennelsClick}
                isLoading={isLoadingKennels}
                className="flex-1"
                subCount={{
                  value: particularCount,
                  label: t("banner.partners.particularsLabel"),
                  isLoading: isLoadingParticulares,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PawMatchBanner;
